import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Avatar, Box, Button, Flex, HStack, Icon, IconButton, Image, Menu, MenuButton, MenuItem, MenuList, Spinner, Text, VStack } from '@chakra-ui/react';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { FiCheck, FiChevronLeft, FiChevronRight, FiClock, FiPackage, FiPlay, FiUsers, FiVideo } from 'react-icons/fi';
import { FaInstagram, FaTiktok } from 'react-icons/fa';
import { Campaign, Client, FoodfluenceLocation, isDeliveryOnlyUgc, windowOf } from '@foodfluence/shared';
import { exploreApi } from '../../../services/api/explore';
import { getInspirationContentInfo } from '../../../services/firebaseService';
import { useAuth } from '../../../contexts/AuthContext';
import { CollabDataContext } from '../../../contexts/collabDataContext';
import { InfluencerCampaignDataContext } from 'contexts/InfluencerCampaignDataContext';
import { InfluencerDataContext } from 'contexts/InfluencerDataContext';
import { mergeInspirationEntries } from '../../../utils/urlUtils';
import { EmptyState } from '../../../components/ui';
import { openCrispChat } from '../../../components/FrostedSupportButton';
import { useInfluencerLocations } from '../../../contexts/InfluencerLocationContext';
import { calculateDistance, getInfluencerPrimaryLocation, getLocationDisplayText } from '../../../utils/locationUtils';
import { ff } from '../../../theme';
import { goLiveLabels } from '../../collabs/utils/postByLabels';
import { getTurnaroundText, getTurnaroundTitle } from '../../collabs/utils/creativeBriefUtils';
import { getMaxPartySize } from '../../collabs/scheduling/utils/partySizeUtils';
import { ImageChip } from '../components/CampaignComponent';
import RequestCollabModal from '../components/RequestCollabModal';
import SignInSignUpModal from '../components/SignInSignUpModal';
import {
  CAMPAIGN_TYPE_CHIP,
  findExistingCollabRequest,
  getCampaignWhereText,
  getBrandImage,
  getDeliverableLines,
  getPendingCollabRequestsForClient,
  showsCredit,
} from '../utils';

type CampaignWithLocations = Campaign & { locations: FoodfluenceLocation[] };
type DetailState = { campaign?: CampaignWithLocations; client?: Client } | null;
type InspoItem = { url: string; isVideo: boolean };

const BRIEF_EXCERPT_LENGTH = 180;
/** Where lists only the nearest stores; the request form still offers every location. */
const NEAREST_LOCATION_COUNT = 3;

/** Platform logo for a deliverable line ("2x IG Reel", "1x TikTok"); UGC deliverables get the video icon. */
const deliverableIcon = (line: string): React.ElementType => {
  if (/tiktok/i.test(line)) return FaTiktok;
  if (/\b(ig|instagram)\b/i.test(line)) return FaInstagram;
  return FiVideo;
};

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text as="h2" textStyle="eyebrow" fontSize="13px" letterSpacing="0.08em">
    {children}
  </Text>
);

const Fact: React.FC<{ icon: React.ElementType; children: React.ReactNode }> = ({ icon, children }) => (
  <HStack as="li" spacing="10px" align="center" fontSize="15px" lineHeight="normal">
    <Icon as={icon} boxSize="18px" flex="none" aria-hidden />
    <Text as="span">{children}</Text>
  </HStack>
);

const HERO_BUTTON = {
  w: '44px',
  h: '44px',
  minW: '44px',
  borderRadius: 'full',
  bg: 'white',
  color: ff.ink,
  _hover: { bg: ff.grey },
} as const;

/** "You get" figure: 44px value, 12px uppercase label, 6px gap (board Campaign.dc). */
const YouGetFigure: React.FC<{ value: number; label: string }> = ({ value, label }) => (
  <HStack as="span" spacing="6px" align="baseline">
    <Text as="span" textStyle="display" fontSize="29px" textTransform="none" letterSpacing="0">
      ${value}
    </Text>
    <Text as="span" textStyle="eyebrow" fontSize="12px" letterSpacing="0.08em">
      {label}
    </Text>
  </HStack>
);

const Check: React.FC<{ icon?: React.ElementType; children: React.ReactNode }> = ({ icon = FiCheck, children }) => (
  <HStack as="li" spacing="10px" align="flex-start" fontSize="15px" lineHeight="1.4">
    <Icon as={icon} boxSize="16px" mt="2px" flex="none" color={ff.accent} strokeWidth={2.6} aria-hidden />
    <Text as="span">{children}</Text>
  </HStack>
);

/** Signed URLs for the brand's scraped inspiration clips (successful downloads only). */
const useInspiration = (campaign: Campaign | null): InspoItem[] => {
  const [items, setItems] = useState<InspoItem[]>([]);
  useEffect(() => {
    const paths = Object.values(campaign?.contentRequirements || {})
      .flatMap(req => mergeInspirationEntries(req.downloadedInspirationContent ?? [], req.inspirationLinks ?? []))
      .filter(entry => entry.status === 'success' && entry.storagePath)
      .map(entry => entry.storagePath as string);
    if (paths.length === 0) {
      setItems([]);
      return;
    }
    let cancelled = false;
    Promise.allSettled(paths.map(path => getInspirationContentInfo(path))).then(results => {
      if (cancelled) return;
      setItems(
        results.flatMap(result =>
          result.status === 'fulfilled' ? [{ url: result.value.url, isVideo: result.value.contentType.startsWith('video') }] : []
        )
      );
    });
    return () => {
      cancelled = true;
    };
  }, [campaign]);
  return items;
};

const CampaignDetailPage: React.FC = () => {
  const { clientId, campaignId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { influencerData } = useContext(InfluencerDataContext);
  const collabData = useContext(CollabDataContext);
  const campaigns = useContext(InfluencerCampaignDataContext);

  const navState = location.state as DetailState;
  const fromState = navState?.campaign?.id === campaignId && navState?.client ? navState : null;
  const [campaign, setCampaign] = useState<CampaignWithLocations | null>(fromState?.campaign ?? null);
  const [client, setClient] = useState<Client | null>(fromState?.client ?? null);
  const [loading, setLoading] = useState(!fromState);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [scrollToCompensation, setScrollToCompensation] = useState(false);
  const [isBriefExpanded, setIsBriefExpanded] = useState(false);
  const [isSignInOpen, setIsSignInOpen] = useState(false);

  // Deep link / refresh: no navigation state, so load the brand's live campaigns and pick this one
  useEffect(() => {
    if (fromState || !clientId) return;
    let cancelled = false;
    exploreApi
      .getClientAndCampaigns({ clientId })
      .then(data => {
        if (cancelled || !data) return;
        setClient(data.client);
        setCampaign(data.campaigns.find(c => c.id === campaignId) ?? null);
      })
      .catch(error => console.error('Error loading campaign:', error))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [clientId, campaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  const inspo = useInspiration(campaign);
  const pendingRequest = useMemo(
    () => findExistingCollabRequest(campaign, getPendingCollabRequestsForClient(collabData, campaigns, client)),
    [campaign, collabData, campaigns, client]
  );

  // Where: the campaign's locations nearest the creator's chosen saved location; the pick pre-fills the request
  const { influencerLocations } = useInfluencerLocations();
  const [nearLocationId, setNearLocationId] = useState<string | null>(null);
  const nearLocation =
    influencerLocations.find(loc => loc.docId === nearLocationId) ?? getInfluencerPrimaryLocation(influencerLocations);
  const sortedLocations = useMemo(
    () =>
      (campaign?.locations ?? [])
        .map(loc => ({
          loc,
          miles: nearLocation ? calculateDistance(nearLocation.lat, nearLocation.lng, loc.lat, loc.lng) : null,
        }))
        .sort((a, b) => (a.miles ?? 0) - (b.miles ?? 0))
        .slice(0, NEAREST_LOCATION_COUNT),
    [campaign, nearLocation]
  );
  const [pickedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const selectedLocationId = pickedLocationId ?? sortedLocations[0]?.loc.docId ?? null;

  const goBack = () => (location.key !== 'default' ? navigate(-1) : navigate(`/explore/client/${clientId}`));

  if (loading) {
    return (
      <Flex minH="100vh" align="center" justify="center">
        <Spinner size="xl" color={ff.ink} />
      </Flex>
    );
  }

  const influencerId = influencerData?.id;
  if (!campaign || !client || (!!influencerId && !!client.mutedInfluencers?.includes(influencerId))) {
    return (
      <Box px="20px" pt="24px" fontFamily="display" color={ff.ink}>
        <EmptyState
          title="Campaign not available"
          body="It may have closed or filled up. The brand may have other campaigns open."
          primary={{ label: 'See the brand', onClick: () => navigate(`/explore/client/${clientId}`) }}
          secondary={{ label: 'Back to Explore', onClick: () => navigate('/explore') }}
        />
      </Box>
    );
  }

  const cash = campaign.cash ?? 0;
  const credit = campaign.credit ?? 0;
  const productValue = Math.round(campaign.productValue ?? 0);
  const withCredit = showsCredit(campaign.campaignType, credit);
  const typeLabel = campaign.campaignType ? CAMPAIGN_TYPE_CHIP[campaign.campaignType] : null;
  const isUgc = campaign.creatorType === 'ugc';
  const noPosting = isUgc && isDeliveryOnlyUgc(null, campaign);

  const deliverables = getDeliverableLines(campaign, campaign.deliverableCounts);
  const goLive = windowOf(campaign);
  const turnaround = getTurnaroundText(campaign);
  const brief = campaign.description?.trim() ?? '';
  const isBriefLong = brief.length > BRIEF_EXCERPT_LENGTH;
  const briefText = isBriefLong && !isBriefExpanded ? `${brief.slice(0, BRIEF_EXCERPT_LENGTH).trimEnd()}…` : brief;
  // Show only the amounts the creator actually gets; $0 cash or $0 credit is left out.
  const showCredit = withCredit && credit > 0;
  const showCash = cash > 0 || (!showCredit && productValue === 0);
  // Product-only offers (e.g. shipped, $0 cash) lead with the product value instead of a chip.
  const productOnly = !showCash && !showCredit && productValue > 0;
  const whereText = getCampaignWhereText(campaign);
  const hasLocationList = campaign.locations?.length > 0 && !!campaign.foodfluenceLocations?.length;

  const openForm = (toCompensation: boolean) => {
    if (!currentUser) {
      setIsSignInOpen(true);
      return;
    }
    setScrollToCompensation(toCompensation);
    setIsFormOpen(true);
  };

  return (
    <Box minH="100vh" bg="white" fontFamily="display" color={ff.ink} pb="150px">
      {/* Hero */}
      <Box position="relative" h="320px" bg={ff.grey}>
        <Image src={getBrandImage(campaign, client)} alt="" w="100%" h="100%" objectFit="cover" />
        {isUgc && (
          <Box position="absolute" bottom="16px" left="16px">
            <ImageChip tone="ink">UGC</ImageChip>
          </Box>
        )}
        <IconButton
          aria-label="Back"
          icon={<FiChevronLeft size={20} strokeWidth={2.2} />}
          onClick={goBack}
          position="absolute"
          top="16px"
          left="16px"
          {...HERO_BUTTON}
        />
        <IconButton
          aria-label="Help"
          icon={<Text as="span" fontSize="18px" fontWeight={600} lineHeight="normal">?</Text>}
          onClick={openCrispChat}
          position="absolute"
          top="16px"
          right="16px"
          {...HERO_BUTTON}
        />
      </Box>

      <VStack px="20px" pt="22px" spacing="26px" align="stretch">
        {/* Brand card */}
        <Flex
          as={RouterLink}
          to={`/explore/client/${client.id}`}
          align="center"
          gap="12px"
          py="10px"
          pl="10px"
          pr="12px"
          border="1px solid"
          borderColor={ff.card}
          borderRadius="18px"
          color={ff.ink}
          _hover={{ textDecoration: 'none' }}
          aria-label={`View ${client.accountName}'s profile`}
        >
          <Avatar src={client.instagramProfilePicture} name={client.accountName} boxSize="48px" bg={ff.grey} color={ff.ink} flex="none" sx={{ '& > [role=img]': { fontSize: '16px', fontWeight: 600 } }} />
          <Text flex={1} minW={0} fontSize="16px" fontWeight={600} lineHeight="normal" noOfLines={1}>
            {client.accountName}
          </Text>
          <Flex as="span" flex="none" h="36px" px="14px" borderRadius="full" bg={ff.lime} color={ff.ink} align="center" gap="4px" fontSize="13px" fontWeight={600}>
            View profile <Icon as={FiChevronRight} boxSize="14px" strokeWidth={2.4} aria-hidden />
          </Flex>
        </Flex>

        {/* You get */}
        <VStack bg={ff.limeSurface} borderRadius="22px" p="18px" spacing="16px" align="stretch">
          <VStack align="stretch" spacing="10px">
            <Text textStyle="eyebrow" fontSize="12px" letterSpacing="0.08em" color={ff.text2}>
              You get
            </Text>
            <Flex align="baseline" gap="18px" wrap="wrap">
              {showCash && <YouGetFigure value={cash} label="Cash" />}
              {showCash && showCredit && (
                <Text fontSize="28px" lineHeight={1.12} fontWeight={300} color={ff.faint} aria-hidden>
                  +
                </Text>
              )}
              {showCredit && <YouGetFigure value={credit} label="Credit" />}
              {productOnly && <YouGetFigure value={productValue} label="Product" />}
              {productValue > 0 && !productOnly && (
                <Text as="span" px="7px" py="3px" borderRadius="6px" bg={ff.lime} color={ff.ink} fontSize="12px" fontWeight={600} lineHeight="normal">
                  +${productValue} product
                </Text>
              )}
            </Flex>
          </VStack>
          <Box h="1px" bg="#E0E0DD" />
          <VStack as="ul" listStyleType="none" align="stretch" spacing="10px" m={0} p={0}>
            {deliverables.map(line => (
              <Fact key={line} icon={deliverableIcon(line)}>{line}</Fact>
            ))}
            {campaign.productName && <Fact icon={FiPackage}>{campaign.productName}</Fact>}
            {campaign.campaignType === 'inStore' && <Fact icon={FiUsers}>Party of up to {getMaxPartySize(campaign)}</Fact>}
            {turnaround && (
              <Fact icon={FiClock}>
                {getTurnaroundTitle(campaign)}: {turnaround}
              </Fact>
            )}
          </VStack>
        </VStack>

        {/* Campaign name */}
        <VStack as="section" aria-labelledby="campaign-name" align="stretch" spacing="4px" px="2px">
          <Text textStyle="eyebrow" fontSize="13px" fontWeight={600} letterSpacing="0.08em" color={ff.text2}>
            Campaign{typeLabel ? ` · ${typeLabel}` : ''}
          </Text>
          <Text as="h1" id="campaign-name" m={0} fontSize="22px" lineHeight="1.25" fontWeight={600}>
            {campaign.name}
          </Text>
        </VStack>

        {/* Brief */}
        <VStack as="section" aria-label="Brief" align="stretch" spacing="14px" border="1px solid" borderColor={ff.card} borderRadius="22px" p="18px">
          <VStack align="stretch" spacing="8px">
            <Text as="h2" m={0} fontSize="15px" fontWeight={600} lineHeight="normal">
              The brief
            </Text>
            {briefText && (
              <Text m={0} fontSize="15px" lineHeight="1.5" color={ff.body} whiteSpace="pre-line">
                {briefText}
              </Text>
            )}
            {isBriefLong && (
            <Button
              variant="unstyled"
              alignSelf="flex-start"
              h="32px"
              minW="auto"
              display="flex"
              alignItems="center"
              gap="4px"
              lineHeight="normal"
              fontSize="14px"
              fontWeight={600}
              color={ff.ink}
              textDecoration="underline"
              aria-expanded={isBriefExpanded}
              onClick={() => setIsBriefExpanded(open => !open)}
            >
              {isBriefExpanded ? 'Show less' : 'Read full brief'}
            </Button>
            )}
          </VStack>
          {(deliverables.length > 0 || noPosting || goLive) && (
            <>
              <Box h="1px" bg={ff.line} />
              <VStack align="stretch" spacing="10px">
                <Text as="h3" m={0} textStyle="eyebrow" fontSize="11px" letterSpacing="0.08em" color={ff.text2}>
                  Deliverables
                </Text>
                <VStack as="ul" listStyleType="none" align="stretch" spacing="8px" m={0} p={0}>
                  {deliverables.map(line => (
                    <Check key={line} icon={deliverableIcon(line)}>{line}</Check>
                  ))}
                  {noPosting && <Check>No posting needed</Check>}
                  {goLive && <Check>{goLiveLabels(goLive).post}</Check>}
                </VStack>
              </VStack>
            </>
          )}
        </VStack>

        {/* Inspo */}
        {inspo.length > 0 && (
          <VStack as="section" align="stretch" spacing="12px">
            <Flex align="baseline" justify="space-between">
              <SectionTitle>Inspo</SectionTitle>
              <Text fontSize="13px" color={ff.text2}>
                The vibe {client.accountName} wants
              </Text>
            </Flex>
            <HStack spacing="8px" overflowX="auto" mx="-20px" px="20px" sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
              {inspo.map((item, i) => (
                <Box
                  as="a"
                  key={item.url}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open inspo ${item.isVideo ? 'video' : 'image'} ${i + 1}`}
                  position="relative"
                  flex="none"
                  w="120px"
                  h="213px"
                  borderRadius="16px"
                  overflow="hidden"
                  bg={ff.grey}
                >
                  {item.isVideo ? (
                    <>
                      <Box as="video" src={`${item.url}#t=0.1`} muted playsInline preload="metadata" w="100%" h="100%" objectFit="cover" />
                      <Flex position="absolute" inset={0} align="center" justify="center">
                        <Flex w="40px" h="40px" borderRadius="full" bg="rgba(255,255,255,0.92)" align="center" justify="center">
                          <Icon as={FiPlay} boxSize="16px" fill={ff.ink} color={ff.ink} aria-hidden />
                        </Flex>
                      </Flex>
                    </>
                  ) : (
                    <Image src={item.url} alt="" w="100%" h="100%" objectFit="cover" />
                  )}
                </Box>
              ))}
            </HStack>
          </VStack>
        )}

        {/* Where */}
        {(whereText || hasLocationList) && (
          <VStack as="section" align="stretch" spacing={0}>
            <Flex align="baseline" justify="space-between" pb="6px">
              <SectionTitle>Where</SectionTitle>
              {hasLocationList && nearLocation && (
                influencerLocations.length > 1 ? (
                  <Menu placement="bottom-end">
                    <MenuButton as={Button} variant="unstyled" h="32px" minW="auto" fontSize="13px" fontWeight={400} lineHeight="normal" color={ff.text2}>
                      Near {nearLocation.city} ·{' '}
                      <Text as="span" color={ff.ink} fontWeight={600} textDecoration="underline">
                        Change
                      </Text>
                    </MenuButton>
                    <MenuList minW="220px" p="6px" borderRadius="16px" border="1px solid" borderColor={ff.line} bg="white" boxShadow="0 12px 32px rgba(14,14,14,0.12)" zIndex={10}>
                      {influencerLocations.map(loc => (
                        <MenuItem
                          key={loc.docId}
                          onClick={() => setNearLocationId(loc.docId)}
                          minH="44px"
                          px="12px"
                          borderRadius="10px"
                          fontSize="15px"
                          color={ff.ink}
                          fontWeight={loc.docId === nearLocation.docId ? 800 : 500}
                          bg={loc.docId === nearLocation.docId ? ff.limeSurface : 'transparent'}
                          _hover={{ bg: loc.docId === nearLocation.docId ? ff.limeSurface : ff.grey }}
                          _focus={{ bg: loc.docId === nearLocation.docId ? ff.limeSurface : ff.grey }}
                        >
                          {getLocationDisplayText(loc)}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                ) : (
                  <Text fontSize="13px" color={ff.text2}>
                    Near {nearLocation.city}
                  </Text>
                )
              )}
            </Flex>
            {hasLocationList ? (
              <VStack role="radiogroup" aria-label="Pick a location" align="stretch" spacing={0}>
                {sortedLocations.map(({ loc, miles }) => {
                  const checked = loc.docId === selectedLocationId;
                  return (
                    <Flex
                      as="button"
                      type="button"
                      role="radio"
                      aria-checked={checked}
                      key={loc.docId}
                      onClick={() => setSelectedLocationId(loc.docId)}
                      align="center"
                      gap="12px"
                      minH="64px"
                      w="100%"
                      textAlign="left"
                      borderTop="1px solid"
                      borderColor={ff.line}
                    >
                      <Box
                        flex="none"
                        w="20px"
                        h="20px"
                        borderRadius="full"
                        border="2px solid"
                        borderColor={checked ? ff.ink : ff.muted}
                        boxShadow={checked ? `inset 0 0 0 4px white` : undefined}
                        bg={checked ? ff.ink : 'white'}
                      />
                      <VStack flex={1} minW={0} align="stretch" spacing="2px">
                        <Text fontSize="15px" fontWeight={600} lineHeight="normal" noOfLines={1}>
                          {loc.name || loc.fullAddress}
                        </Text>
                        {loc.name && (
                          <Text fontSize="13px" lineHeight="normal" color={ff.text2} noOfLines={1}>
                            {loc.fullAddress}
                          </Text>
                        )}
                      </VStack>
                      {miles !== null && (
                        <Text fontSize="13px" color={ff.text2} flex="none">
                          {miles < 10 ? miles.toFixed(1) : Math.round(miles)} mi
                        </Text>
                      )}
                    </Flex>
                  );
                })}
              </VStack>
            ) : (
              <Text fontSize="15px">{whereText}</Text>
            )}
          </VStack>
        )}
      </VStack>

      {/* Floating CTA bar (MainLayout hides the tab bar on this route) */}
      <Box position="fixed" left="12px" right="12px" bottom="calc(20px + env(safe-area-inset-bottom))" zIndex={20}>
        <Flex maxW="560px" mx="auto" bg={ff.ink} color="white" borderRadius="26px" py="10px" pr="10px" pl="20px" align="center" gap="12px">
          <VStack flex={1} minW={0} align="flex-start" spacing={0}>
            <Text textStyle="display" fontSize="24px" lineHeight="1" letterSpacing="0" textTransform="none" color={ff.lime} noOfLines={1}>
              {showCash && `$${cash} cash`}
              {showCredit && (showCash ? <Text as="span" color="#CFCFD2"> + ${credit} credit</Text> : `$${credit} credit`)}
              {productValue > 0 && (productOnly ? `$${productValue} product` : <Text as="span" color="#CFCFD2"> + ${productValue} product</Text>)}
            </Text>
            {pendingRequest ? (
              <Text fontSize="13px" color="#CFCFD2" h="28px" display="flex" alignItems="center">
                Waiting on {client.accountName}'s reply
              </Text>
            ) : (
              // UGC deliverables are the brand's; only the rate can be proposed, so say that
              <Button variant="link" h="28px" minW="auto" color="#CFCFD2" fontSize="13px" fontWeight={400} lineHeight="normal" textDecoration="underline" _hover={{ color: '#CFCFD2' }} onClick={() => openForm(true)}>
                {Object.keys(campaign.contentRequirements ?? {}).length > 0 ? 'Propose a rate' : 'Counter-offer'}
              </Button>
            )}
          </VStack>
          {pendingRequest ? (
            <Button h="56px" px="22px" borderRadius="18px" bg="#2A2A2C" color="#CFCFD2" fontSize="16px" fontWeight={600} isDisabled _disabled={{ opacity: 1, cursor: 'not-allowed' }} _hover={{ bg: '#2A2A2C' }}>
              Requested
            </Button>
          ) : (
            <Button variant="lime" h="56px" px="22px" borderRadius="18px" fontSize="16px" fontWeight={600} onClick={() => openForm(false)}>
              Request
            </Button>
          )}
        </Flex>
      </Box>

      <RequestCollabModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        campaign={campaign}
        client={client}
        requestedCollab={pendingRequest}
        scrollToCompensation={scrollToCompensation}
        initialSelectedLocationIds={selectedLocationId ? [selectedLocationId] : undefined}
      />
      <SignInSignUpModal
        isOpen={isSignInOpen}
        onClose={() => setIsSignInOpen(false)}
        returnTo={`/explore/client/${client.id}/request-collab`}
        returnState={{ campaign, client }}
      />
    </Box>
  );
};

export default CampaignDetailPage;
